import React, { useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, IconButton, Chip, CircularProgress,
  Alert, Collapse, Tooltip, Stack,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import SyncIcon from '@mui/icons-material/Sync';
import RateReviewIcon from '@mui/icons-material/RateReview';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { dmsApi } from '../services/dmsApi';
import { useAgentSuggestions } from '../hooks/useAgentSuggestions';
import useDmsPalette from '../hooks/useDmsPalette';

const QUICK_PROMPTS = {
  manual: [
    'Improve clarity of the active chapter',
    'Add a warning about data security',
    'Simplify steps for beginners',
  ],
  'technical-manual': [
    'Sync with latest platform features',
    'Update API documentation section',
    'Add deployment checklist',
  ],
  document: [
    'Improve title and description',
    'Suggest better tags',
    'Summarize for executives',
  ],
};

export default function AgentCopilot({
  mode = 'manual',
  content,
  selection,
  classification = 'internal',
  chapterId,
  documentId,
  onApplyPatches,
  onApplyDocumentPatches,
  onReviewComplete,
  showSync = false,
  collapsed: defaultCollapsed = false,
}) {
  const MP = useDmsPalette();
  const [instruction, setInstruction] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const { suggestions, loading: suggLoading } = useAgentSuggestions({
    mode,
    content,
    selection,
    documentId,
    chapterId,
    enabled: !collapsed && !!content,
  });

  const runEdit = async (text) => {
    const cmd = text || instruction;
    if (!cmd.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await dmsApi.agentEdit({
        mode,
        content,
        selection,
        instruction: cmd,
        classification,
        context: { chapterId, documentId },
      });
      setResult(res);
      if (!text) setInstruction('');
    } catch (err) {
      setError(err.error || 'Agent edit failed');
    } finally {
      setLoading(false);
    }
  };

  const runSync = async () => {
    setSyncLoading(true);
    setError(null);
    try {
      const res = await dmsApi.agentSync({ mode, content });
      setResult(res);
    } catch (err) {
      setError(err.error || 'Platform sync failed');
    } finally {
      setSyncLoading(false);
    }
  };

  const runReview = async () => {
    setReviewLoading(true);
    setError(null);
    try {
      const res = await dmsApi.agentReview({
        mode,
        content,
        documentId,
        reviewType: 'publish',
      });
      onReviewComplete?.(res);
      setResult({ ...res, isReview: true });
    } catch (err) {
      setError(err.error || 'Agent review failed');
    } finally {
      setReviewLoading(false);
    }
  };

  const handleApply = () => {
    if (!result?.patches?.length) return;
    if (mode === 'document' && onApplyDocumentPatches) {
      onApplyDocumentPatches(result.patches);
    } else if (onApplyPatches) {
      onApplyPatches(result.patches);
    }
    setResult(null);
  };

  if (collapsed) {
    return (
      <Tooltip title="Open Agent Copilot">
        <IconButton
          onClick={() => setCollapsed(false)}
          sx={{
            position: 'fixed', right: 24, bottom: 90, zIndex: 1200,
            bgcolor: MP.green, color: '#000', width: 52, height: 52,
            boxShadow: `0 4px 20px ${MP.green}66`,
            '&:hover': { bgcolor: MP.green },
          }}
        >
          <AutoAwesomeIcon />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Paper
      elevation={4}
      sx={{
        position: 'sticky',
        top: 80,
        p: 2,
        bgcolor: MP.deepBlue,
        border: `1px solid ${MP.border}`,
        borderRadius: 2,
        maxHeight: 'calc(100vh - 100px)',
        overflow: 'auto',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <AutoAwesomeIcon sx={{ color: MP.green }} />
        <Typography variant="subtitle1" fontWeight={700}>Agent Copilot</Typography>
        <Chip label="Apollo" size="small" sx={{ bgcolor: `${MP.blue}33`, color: MP.blue, fontSize: '0.65rem' }} />
        <IconButton size="small" onClick={() => setCollapsed(true)} sx={{ ml: 'auto' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Proactive suggestions — Phase 3 */}
      {(suggestions.length > 0 || suggLoading) && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>Proactive</Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
            {suggLoading && <CircularProgress size={14} />}
            {suggestions.map((s) => (
              <Chip
                key={s.id}
                label={s.message}
                size="small"
                onClick={() => runEdit(s.action || s.message)}
                sx={{ fontSize: '0.68rem', cursor: 'pointer', maxWidth: '100%' }}
                color={s.type === 'warning' ? 'warning' : s.type === 'compliance' ? 'error' : 'default'}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Quick prompts */}
      <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1.5 }}>
        {(QUICK_PROMPTS[mode] || QUICK_PROMPTS.manual).map((p) => (
          <Chip key={p} label={p} size="small" variant="outlined" onClick={() => runEdit(p)} sx={{ fontSize: '0.68rem', cursor: 'pointer' }} />
        ))}
      </Stack>

      <TextField
        fullWidth
        multiline
        minRows={2}
        maxRows={4}
        size="small"
        placeholder="Ask agents to edit… e.g. 'Rewrite intro for clarity'"
        value={instruction}
        onChange={(e) => setInstruction(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); runEdit(); } }}
        sx={{ mb: 1 }}
      />

      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          size="small"
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
          onClick={() => runEdit()}
          disabled={loading || !instruction.trim()}
          sx={{ bgcolor: MP.green, color: '#000', '&:hover': { bgcolor: MP.green } }}
        >
          Run Agents
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={reviewLoading ? <CircularProgress size={14} /> : <RateReviewIcon />}
          onClick={runReview}
          disabled={reviewLoading}
        >
          Review
        </Button>
        {showSync && (
          <Button
            variant="outlined"
            size="small"
            startIcon={syncLoading ? <CircularProgress size={14} /> : <SyncIcon />}
            onClick={runSync}
            disabled={syncLoading}
            color="info"
          >
            Sync Platform
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

      <Collapse in={!!result}>
        {result && (
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'rgba(76,175,80,0.08)', borderRadius: 1, border: '1px solid rgba(76,175,80,0.3)' }}>
            <Typography variant="caption" fontWeight={700} color="success.main">
              {result.isReview ? 'Review complete' : 'Agents propose changes'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>{result.summary}</Typography>
            <Stack direction="row" gap={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
              {(result.agents || []).map((a) => (
                <Chip key={a} label={a} size="small" sx={{ fontSize: '0.65rem' }} />
              ))}
              {result.confidence != null && (
                <Chip label={`${Math.round(result.confidence * 100)}% confident`} size="small" variant="outlined" />
              )}
            </Stack>
            {result.requiresApproval && (
              <Alert severity="warning" sx={{ mt: 1, py: 0 }}>Restricted content — review before applying</Alert>
            )}
            {!result.isReview && result.patches?.length > 0 && (
              <Button
                size="small"
                variant="contained"
                startIcon={<CheckIcon />}
                onClick={handleApply}
                sx={{ mt: 1 }}
                disabled={result.requiresApproval}
              >
                Apply {result.patches.length} patch(es)
              </Button>
            )}
            {result.isReview && onReviewComplete && (
              <Button size="small" sx={{ mt: 1 }} onClick={() => onReviewComplete(result)}>View full report</Button>
            )}
          </Box>
        )}
      </Collapse>
    </Paper>
  );
}